import React, { Key, useState, useEffect } from "react";
import moment from "moment";
import { Card as CardUI } from "@wellth/web-ui";
import Button, { ButtonProps } from "antd/lib/button";
import CARD_STYLES from "../styles";

const IMAGE_STYLES: React.CSSProperties = {
  width: "15rem",
  height: "26.25rem",
};

const PHOTO_GALLERY: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  gap: "0.5rem",
  overflowX: "auto",
  whiteSpace: "nowrap",
  paddingBottom: "0.5rem",
  width: "240px",
  height: "120px",
};

const PHOTO_LINK_STYLE: React.CSSProperties = {
  color: "#1890ff",
  cursor: "pointer",
  textDecoration: "underline",
  fontSize: "0.875rem",
};

const CARD_STYLE: React.CSSProperties = {
  alignSelf: "center",
  margin: "0.625rem 0 0.625rem 0",
  width: "55rem",
  ...CARD_STYLES.card,
};

const BODY_STYLE: React.CSSProperties = {
  backgroundColor: "white",
  borderRadius: 10,
  padding: 0,
};

const DETAIL_LABEL_STYLE: React.CSSProperties = {
  color: "rgba(0, 0, 0, 0.45)",
  marginRight: "0.375rem",
};

const DETAIL_VALUE_STYLE: React.CSSProperties = {
  color: "rgba(0, 0, 0, 0.85)",
};

const SUBMISSION_INFO_STYLE: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  gap: "1.25rem",
  padding: "1rem 1rem 0",
};

const DETAILS_COLUMN_STYLE: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
};

const DETAILS_STYLE: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.25rem",
  color: "rgba(0, 0, 0, 0.85)",
  fontSize: "0.75rem",
};

const DETAILS_TITLE_STYLE: React.CSSProperties = {
  fontSize: "1rem",
  fontWeight: 600,
  marginBottom: "0.375rem",
  color: "rgba(0, 0, 0, 0.85)",
};

const REVIEW_INPUT_WRAPPER_STYLE: React.CSSProperties = {
  marginTop: "0.625rem",
};

const FOOTER_STYLE: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  flexDirection: "row",
  gap: "0.5rem",
  padding: "0 1rem 1rem",
};

type FooterButtonProps = ButtonProps & { key: Key; title: string };

interface DetailsProps {
  submittedOn: Date;
  submittedBy: string;
  programCode: string;
  reward: string;
  submissionId: string;
  routeToMemberPage: () => void;
}

const Details: React.FC<DetailsProps & { title?: string }> = ({
  title,
  submittedOn,
  submittedBy,
  programCode,
  reward,
  submissionId,
  routeToMemberPage,
}) => (
  <div style={DETAILS_STYLE}>
    {title && <div style={DETAILS_TITLE_STYLE}>{title}</div>}
    <div>
      <span style={DETAIL_LABEL_STYLE}>Submitted On:</span>
      <span style={DETAIL_VALUE_STYLE}>
        {moment(submittedOn).format("h:mm A z on MMMM D, YYYY")}
      </span>
    </div>
    <div>
      <span style={DETAIL_LABEL_STYLE}>Submitted By:</span>
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          routeToMemberPage();
        }}
        style={{ cursor: "pointer" }}
      >
        {submittedBy}
      </a>
    </div>
    <div>
      <span style={DETAIL_LABEL_STYLE}>Program Code:</span>
      <span style={DETAIL_VALUE_STYLE}>{programCode}</span>
    </div>
    <div>
      <span style={DETAIL_LABEL_STYLE}>Incentive Amount:</span>
      <span style={DETAIL_VALUE_STYLE}>{reward}</span>
    </div>
    <div>
      <span style={DETAIL_LABEL_STYLE}>Submission Id:</span>
      <span style={DETAIL_VALUE_STYLE}>{submissionId}</span>
    </div>
  </div>
);

// Hook to detect if URL is an image or PDF
const useFileType = (url: string): "image" | "pdf" | "loading" => {
  const [fileType, setFileType] = useState<"image" | "pdf" | "loading">(
    "loading",
  );

  useEffect(
    () => {
      let isActive = true;
      const img = new Image();
      img.onload = () => {
        if (isActive) setFileType("image");
      };
      img.onerror = () => {
        if (isActive) setFileType("pdf");
      };
      img.src = url;
      return () => {
        isActive = false;
        img.onload = null;
        img.onerror = null;
        img.src = "";
      };
    },
    [url],
  );

  return fileType;
};


const PhotoRow: React.FC<{
  url: string;
  isSelected: boolean;
  onSelect: (url: string, fileType: "image" | "pdf") => void;
}> = ({ url, isSelected, onSelect }) => {
  const fileType = useFileType(url);

  if (fileType === "loading") {
    return <span>Loading...</span>;
  }

  const onClick = (e: React.MouseEvent): void => {
    e.preventDefault();
    onSelect(url, fileType);
  };

  if (fileType === "pdf") {
    return (
       <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        Document
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      style={{ padding: 0, border: "none", background: "none" }}
    >
      <img
        src={url}
        alt="Photo"
        style={{
          width: "120px",
          height: "120px",
          objectFit: "cover",
          borderRadius: "4px",
          cursor: "pointer",
          border: isSelected ? "2px solid #1890ff" : "2px solid transparent",
        }}
      />
    </button>
  );
};

export interface SubmissionReviewCardProps {
  title?: string;
  style?: React.CSSProperties;
  bodyStyle?: React.CSSProperties;
  innerBodyStyle?: React.CSSProperties;
  buttonTitle?: string;
  popconfirm?: boolean;
  placeholder?: string;
  popconfirmTitle?: string;
  photoUrl: string;
  photoUrls?: string[];
  footerButtons: FooterButtonProps[];
  details: DetailsProps;
}

export const SubmissionReviewCard: React.FC<SubmissionReviewCardProps> = ({
  title,
  children,
  footerButtons,
  photoUrl,
  photoUrls,
  details: {
    submittedOn,
    submittedBy,
    programCode,
    reward,
    submissionId,
    routeToMemberPage,
  },
}) => {
  const photoURLsCombo = [photoUrl, ...(photoUrls ?? [])];

  const validPhotoURLsCombo = photoURLsCombo?.filter((url) => Boolean(url));
  const initialSelectedUrl = validPhotoURLsCombo?.[0] || "";

  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState<string>(
    initialSelectedUrl,
  );
  const [selectedFileType, setSelectedFileType] = useState<
    "image" | "pdf" | null
  >(null);
  const initialFileType = useFileType(initialSelectedUrl);
  const fileType = selectedFileType ?? initialFileType;

  const onSelectPhoto = (url: string, type: "image" | "pdf"): void => {
    setSelectedPhotoUrl(url);
    setSelectedFileType(type);
  };

  let renderPhoto: React.ReactNode = null;
  if (selectedPhotoUrl) {
    const openPhoto = (e: React.MouseEvent): void => {
      e.preventDefault();
      window.open(selectedPhotoUrl, "_blank", "noopener,noreferrer");
    };
    if (fileType === "pdf") {
      renderPhoto = (
        <a href="#" style={PHOTO_LINK_STYLE} onClick={openPhoto}>
          Document
        </a>
      );
    } else if (fileType === "image") {
      renderPhoto = (
        <a
          href="#"
          onClick={openPhoto}
          style={{ cursor: "pointer", display: "block" }}
        >
          <img style={IMAGE_STYLES} src={selectedPhotoUrl} alt="submission" />
        </a>
      );
    }
  }

  return (
    <CardUI
      headStyle={CARD_STYLES.head}
      bodyStyle={BODY_STYLE}
      style={CARD_STYLE}
    >
      <div style={SUBMISSION_INFO_STYLE}>
        <div>
          {renderPhoto}

          {validPhotoURLsCombo && validPhotoURLsCombo.length > 0 && (
            <div style={PHOTO_GALLERY}>
              {validPhotoURLsCombo.map((url) => (
                <PhotoRow
                  key={url}
                  url={url}
                  isSelected={url === selectedPhotoUrl}
                  onSelect={onSelectPhoto}
                />
              ))}
            </div>
          )}
        </div>

        <div style={DETAILS_COLUMN_STYLE}>
          <Details
            title={title}
            submittedOn={submittedOn}
            submittedBy={submittedBy}
            programCode={programCode}
            reward={reward}
            submissionId={submissionId}
            routeToMemberPage={routeToMemberPage}
          />
          <div style={REVIEW_INPUT_WRAPPER_STYLE}>{children}</div>
        </div>
      </div>

      <div style={FOOTER_STYLE}>
        {footerButtons.map(({ title: buttonTitle, key, ...buttonProps }) => (
          <Button key={key} type="primary" {...buttonProps}>
            {buttonTitle}
          </Button>
        ))}
      </div>
    </CardUI>
  );
};

export default SubmissionReviewCard;
